import { supabase } from "./supabase";
import { Subscription } from "../types/blog";
import { Resend } from "resend";

// Initialize Resend with API key
const resendApiKey = import.meta.env.VITE_RESEND_API_KEY;
const resend = new Resend(resendApiKey);

// Email configuration
const fromEmail = import.meta.env.VITE_EMAIL_FROM || "onboarding@resend.dev";
const fromName = import.meta.env.VITE_EMAIL_FROM_NAME || "Your Blog";

// Add a new subscriber to the database
export async function addSubscriber(
  email: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Check if the email already exists
    const { data: existingSubscriber } = await supabase
      .from("subscriptions")
      .select("*")
      .eq("email", email)
      .single();

    if (existingSubscriber) {
      return { success: false, error: "Email already subscribed" };
    }

    // Add the new subscriber
    const { error } = await supabase
      .from("subscriptions")
      .insert([{ email, date: new Date().toISOString() }]);

    if (error) {
      console.error("Error adding subscriber:", error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error("Error in addSubscriber:", error);
    return { success: false, error: "An unexpected error occurred" };
  }
}

// Get all subscribers with new subscriber detection
export async function getAllSubscribers(): Promise<Subscription[]> {
  try {
    const { data, error } = await supabase
      .from("subscriptions")
      .select("*")
      .order("date", { ascending: false });

    if (error) {
      console.error("Error fetching subscribers:", error);
      throw new Error(`Failed to fetch subscribers: ${error.message}`);
    }

    // Mark subscribers as new if they joined in the last 7 days
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    return (data || []).map((subscription: any) => ({
      ...subscription,
      isNew: new Date(subscription.date) > sevenDaysAgo,
    })) as Subscription[];
  } catch (error) {
    console.error("Error in getAllSubscribers:", error);
    throw error;
  }
}

// Remove a subscriber
export async function removeSubscriber(email: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from("subscriptions")
      .delete()
      .eq("email", email);

    if (error) {
      console.error("Error removing subscriber:", error);
      throw new Error(`Failed to remove subscriber: ${error.message}`);
    }

    return true;
  } catch (error) {
    console.error("Error in removeSubscriber:", error);
    throw error;
  }
}

// Send email to subscribers using Resend
export async function sendEmailToSubscribers(
  subject: string,
  htmlContent: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Get all subscribers
    const subscribers = await getAllSubscribers();

    if (subscribers.length === 0) {
      return { success: true, error: "No subscribers to send to" };
    }

    // Track successful sends
    let successCount = 0;
    let errorMessages: string[] = [];

    // Send emails in batches to avoid rate limits
    // Resend recommends sending emails individually for better tracking
    for (const subscriber of subscribers) {
      try {
        const { data, error } = await resend.emails.send({
          from: `${fromName} <${fromEmail}>`,
          to: subscriber.email,
          subject: subject,
          html: htmlContent,
        });

        if (error) {
          console.error(`Error sending to ${subscriber.email}:`, error);
          errorMessages.push(
            `Failed to send to ${subscriber.email}: ${error.message}`
          );
        } else {
          successCount++;
        }
      } catch (err) {
        console.error(`Exception sending to ${subscriber.email}:`, err);
        errorMessages.push(`Exception sending to ${subscriber.email}`);
      }
    }

    // Log results
    console.log(
      `Email sending complete. Success: ${successCount}/${subscribers.length}`
    );

    // Return success if at least one email was sent
    if (successCount > 0) {
      // Record the notification in the database
      try {
        await supabase.from("email_notifications").insert([
          {
            post_id: "system", // This is for a system notification, not tied to a specific post
            post_title: subject,
            recipient_count: successCount,
            sent_at: new Date().toISOString(),
          },
        ]);
      } catch (err) {
        console.error("Error recording notification:", err);
      }

      return {
        success: true,
        error:
          errorMessages.length > 0
            ? `Sent to ${successCount}/${subscribers.length} subscribers. Some errors occurred.`
            : undefined,
      };
    } else {
      return {
        success: false,
        error: "Failed to send any emails. Errors: " + errorMessages.join("; "),
      };
    }
  } catch (error) {
    console.error("Error in sendEmailToSubscribers:", error);
    return {
      success: false,
      error: "Failed to send emails due to a system error",
    };
  }
}

// Send email notification for a new post using the Supabase Edge Function
export async function sendPostNotification(
  postId: string,
  postTitle: string,
  postExcerpt: string,
  postUrl: string,
  postContent: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Get the Supabase URL from the environment
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (!supabaseUrl) {
      return { success: false, error: "Supabase URL not configured" };
    }

    if (!supabaseAnonKey) {
      return { success: false, error: "Supabase Anon Key not configured" };
    }

    if (sessionError) {
      console.error("Error fetching auth session:", sessionError);
      return { success: false, error: "Failed to verify admin session" };
    }

    if (!session?.access_token) {
      return {
        success: false,
        error: "You must be signed in as an admin to send notifications",
      };
    }

    // Ensure the postUrl is correctly formatted and points to the specific post
    // This is important to make sure the email links to the specific post
    console.log("Sending notification with post URL:", postUrl);
    console.log("Including full post content in email");

    // Call the Supabase Edge Function with authorization
    const response = await fetch(`${supabaseUrl}/functions/v1/send-email`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
        apikey: supabaseAnonKey,
      },
      body: JSON.stringify({
        postId,
        postTitle,
        postExcerpt,
        postUrl,
        postContent, // Include the full post content
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Error response from send-email function:", errorText);
      return {
        success: false,
        error: `Server error: ${response.status} ${response.statusText}`,
      };
    }

    const result = await response.json();

    return {
      success: result.success,
      error: result.message || result.error,
    };
  } catch (error) {
    console.error("Error in sendPostNotification:", error);
    return {
      success: false,
      error: "Failed to send post notification due to a network error",
    };
  }
}

// Generate email content for a new post (kept for reference, but not used directly)
export function generateNewPostEmail(
  postTitle: string,
  postExcerpt: string,
  postUrl: string
): string {
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>New Post: ${postTitle}</title>
      <style>
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
          line-height: 1.6;
          color: #333;
          max-width: 600px;
          margin: 0 auto;
          padding: 20px;
        }
        .header {
          background-color: #4f46e5;
          color: white;
          padding: 20px;
          border-radius: 8px 8px 0 0;
          text-align: center;
        }
        .content {
          background-color: #f9fafb;
          padding: 20px;
          border-radius: 0 0 8px 8px;
          border: 1px solid #e5e7eb;
          border-top: none;
        }
        .post-title {
          color: #1e293b;
          font-size: 24px;
          margin-bottom: 10px;
        }
        .post-excerpt {
          color: #4b5563;
          margin-bottom: 20px;
        }
        .button {
          display: inline-block;
          background-color: #4f46e5;
          color: white;
          padding: 10px 20px;
          text-decoration: none;
          border-radius: 5px;
          font-weight: 500;
        }
        .footer {
          margin-top: 30px;
          text-align: center;
          font-size: 14px;
          color: #6b7280;
        }
        .footer a {
          color: #4f46e5;
          text-decoration: none;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>New Post Published!</h1>
      </div>
      <div class="content">
        <h2 class="post-title">${postTitle}</h2>
        <p class="post-excerpt">${postExcerpt}</p>
        <a href="${postUrl}" class="button">Read the full post</a>
      </div>
      <div class="footer">
        <p>You're receiving this email because you subscribed to updates from our blog.</p>
        <p><a href="{unsubscribe_url}">Unsubscribe</a></p>
      </div>
    </body>
    </html>
  `;
}
